import * as React from "react";

import * as Divider from "../components/divider";
import { cn } from "../utils/cn";

const Table = React.forwardRef<React.ElementRef<"table">, React.ComponentPropsWithoutRef<"table">>(
  ({ className, ...rest }, forwardedRef) => {
    return (
      <div className={cn("w-full overflow-x-auto", className)}>
        <table ref={forwardedRef} className="w-full" {...rest} />
      </div>
    );
  }
);
Table.displayName = "Table";
const TableHeader = React.forwardRef<
  React.ElementRef<"thead">,
  React.ComponentPropsWithoutRef<"thead">
>(({ ...rest }, forwardedRef) => {
  return <thead ref={forwardedRef} {...rest} />;
});
TableHeader.displayName = "TableHeader";

const TableHead = React.forwardRef<React.ElementRef<"th">, React.ComponentPropsWithoutRef<"th">>(
  ({ className, ...rest }, forwardedRef) => {
    return (
      <th
        ref={forwardedRef}
        className={cn(
          "bg-bg-weak-50 px-3 py-2 text-left text-paragraph-sm first:rounded-l-lg last:rounded-r-lg",
          className
        )}
        style={{
          fontSize: "14px",
          fontWeight: "400",
          color: "#525866",
          background: "#F5F7FA",
          height: "36px",
        }}
        {...rest}
      />
    );
  }
);
TableHead.displayName = "TableHead";

const TableBody = React.forwardRef<
  React.ElementRef<"tbody">,
  React.ComponentPropsWithoutRef<"tbody"> & {
    spacing?: number;
  }
>(({ spacing = 8, ...rest }, forwardedRef) => {
  return (
    <>
      <tbody aria-hidden="true" className="table-row" style={{ height: spacing }} />
      <tbody ref={forwardedRef} {...rest} />
    </>
  );
});
TableBody.displayName = "TableBody";

const TableRow = React.forwardRef<React.ElementRef<"tr">, React.ComponentPropsWithoutRef<"tr">>(
  ({ className, ...rest }, forwardedRef) => {
    return <tr ref={forwardedRef} className={cn("group/row", className)} {...rest} />;
  }
);
TableRow.displayName = "TableRow";

function TableRowDivider({
  className,
  dividerClassName,
  ...rest
}: React.ComponentPropsWithoutRef<typeof Divider.Root> & {
  dividerClassName?: string;
}) {
  return (
    <tr aria-hidden="true" className={className}>
      <td colSpan={999} className="py-1">
        <Divider.Root variant="line-spacing" className={dividerClassName} {...rest} />
      </td>
    </tr>
  );
}
TableRowDivider.displayName = "TableRowDivider";

const TableCell = React.forwardRef<React.ElementRef<"td">, React.ComponentPropsWithoutRef<"td">>(
  ({ className, ...rest }, forwardedRef) => {
    return (
      <td
        ref={forwardedRef}
        className={cn(
          "h-{48px} py-3 px-3 text-[#525866] text-[14px] transition duration-200 ease-out first:rounded-l-xl last:rounded-r-xl group-hover/row:bg-bg-weak-50",
          className
        )}
        {...rest}
      />
    );
  }
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  React.ElementRef<"caption">,
  React.ComponentPropsWithoutRef<"caption">
>(({ className, ...rest }, forwardedRef) => {
  return (
    <caption
      ref={forwardedRef}
      className={cn("mt-4 text-paragraph-sm text-text-sub-600", className)}
      {...rest}
    />
  );
});
TableCaption.displayName = "TableCaption";

export {
  Table as Root,
  TableHeader as Header,
  TableBody as Body,
  TableHead as Head,
  TableRow as Row,
  TableRowDivider as RowDivider,
  TableCell as Cell,
  TableCaption as Caption,
};
